// Comprehensive performance optimizations
import { performanceUtils } from './performance';

// Critical CSS inlining - محسن للأداء
export const criticalCSS = `
  .critical-hidden { opacity: 0; }
  .critical-visible { opacity: 1; transition: opacity 0.3s ease; }
  .loading-spinner { 
    animation: spin 1s linear infinite; 
    border: 2px solid #f3f4f6; 
    border-top: 2px solid #8b5cf6; 
    border-radius: 50%; 
  }
  @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
  
  /* تحسينات للأجهزة المحمولة */
  @media (max-width: 768px) {
    .mobile-optimized { touch-action: manipulation; }
    .mobile-optimized * { -webkit-tap-highlight-color: transparent; }
  }
  
  /* تحسينات للتفاعل */
  .interactive-optimized {
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
  }
`;

// Resource hints for better loading - محسن للتحميل
export const resourceHints = {
  preconnect: [
    'https://fonts.googleapis.com',
    'https://fonts.gstatic.com'
  ],
  dnsPrefetch: [
    'https://api.example.com'
  ],
  preload: [
    '/fonts/tajawal-regular.woff2'
  ]
};

// Service Worker registration - محسن للتخزين المؤقت
export const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('SW registered: ', registration);
    } catch (error) {
      console.log('SW registration failed: ', error);
    }
  }
};

// Intersection Observer for lazy loading - محسن للتحميل التدريجي
export const createIntersectionObserver = (
  callback: IntersectionObserverCallback,
  options: IntersectionObserverInit = {}
) => {
  const defaultOptions: IntersectionObserverInit = {
    root: null,
    rootMargin: '50px',
    threshold: 0.1,
    ...options
  };

  return new IntersectionObserver(callback, defaultOptions);
};

// Virtual scrolling utilities - محسن للقوائم الطويلة
export const virtualScrolling = {
  calculateVisibleRange: (
    scrollTop: number,
    containerHeight: number,
    itemHeight: number,
    totalItems: number
  ) => {
    const startIndex = Math.floor(scrollTop / itemHeight);
    const endIndex = Math.min(
      startIndex + Math.ceil(containerHeight / itemHeight) + 1,
      totalItems
    );
    
    return { startIndex, endIndex };
  },

  getVisibleItems: <T>(
    items: T[],
    containerHeight: number,
    itemHeight: number,
    scrollTop: number
  ) => {
    const startIndex = Math.floor(scrollTop / itemHeight);
    const endIndex = Math.min(
      startIndex + Math.ceil(containerHeight / itemHeight) + 1,
      items.length
    );
    
    return {
      items: items.slice(startIndex, endIndex),
      startIndex,
      endIndex,
      totalHeight: items.length * itemHeight,
      offsetY: startIndex * itemHeight
    };
  },

  optimizeScroll: (container: HTMLElement) => {
    let ticking = false;
    
    const updateScroll = () => {
      // Update scroll position
      ticking = false;
    };
    
    const requestTick = () => {
      if (!ticking) {
        requestAnimationFrame(updateScroll);
        ticking = true;
      }
    };
    
    container.addEventListener('scroll', requestTick, { passive: true });
    
    return () => {
      container.removeEventListener('scroll', requestTick);
    };
  }
};

// Memory management - محسن لإدارة الذاكرة
export const memoryManagement = {
  // Clean up event listeners
  cleanupEventListeners: (element: Element) => {
    const clone = element.cloneNode(true);
    element.parentNode?.replaceChild(clone, element);
  },

  // Clear unused references
  clearUnusedReferences: () => {
    if (typeof window !== 'undefined') {
      // Clear image cache
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => {
            if (name.includes('image-cache')) {
              caches.delete(name);
            }
          });
        });
      }
    }
  },

  // Monitor memory usage
  monitorMemoryUsage: () => {
    const memory = performanceUtils.getMemoryUsage();
    if (memory && memory.used > memory.limit * 0.8) {
      console.warn('High memory usage detected:', memory);
      memoryManagement.clearUnusedReferences();
    }
  },

  // Throttle scroll events - محسن لأحداث التمرير
  throttleScroll: (callback: () => void) => {
    let ticking = false;
    
    return () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          callback();
          ticking = false;
        });
        ticking = true;
      }
    };
  },

  // Monitor memory usage
  monitorMemory: (callback: (usage: { used: number; total: number; limit: number }) => void) => {
    if (typeof window === 'undefined' || !('memory' in performance)) return;
    
    const memory = (performance as Performance & { memory: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number } }).memory;
    
    const checkMemory = () => {
      const usage = {
        used: Math.round(memory.usedJSHeapSize / 1048576 * 100) / 100,
        total: Math.round(memory.totalJSHeapSize / 1048576 * 100) / 100,
        limit: Math.round(memory.jsHeapSizeLimit / 1048576 * 100) / 100
      };
      
      callback(usage);
    };
    
    const interval = setInterval(checkMemory, 10000); // تحسين الفاصل الزمني
    
    return () => clearInterval(interval);
  },

  // Clean up unused resources
  cleanup: () => {
    // Force garbage collection if available
    if (typeof window !== 'undefined' && 'gc' in window) {
      (window as Window & { gc: () => void }).gc();
    }
  }
};

// Bundle optimization - محسن للحزم
export const bundleOptimization = {
  // Lazy load components
  lazyLoad: (importFn: () => Promise<unknown>) => {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Lazy load timeout'));
      }, 5000);

      importFn()
        .then((module) => {
          clearTimeout(timeout);
          resolve(module);
        })
        .catch((error) => {
          clearTimeout(timeout);
          reject(error);
        });
    });
  },

  // Preload critical resources
  preloadCritical: () => {
    if (typeof window === 'undefined') return;
    
    const criticalResources = [
      '/fonts/tajawal-regular.woff2',
      '/logo-light.png'
    ];

    criticalResources.forEach(resource => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = resource;
      link.as = resource.endsWith('.woff2') ? 'font' : 'image';
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    });
  }
};

// Animation optimization - محسن للرسوم المتحركة
export const animationOptimization = {
  // Optimize animations for performance
  optimizeAnimation: (element: HTMLElement, properties: string[] = ['transform', 'opacity']) => {
    if (typeof window === 'undefined') return;
    
    element.style.willChange = properties.join(', ');
    
    // Clean up will-change after animation
    const cleanup = () => {
      element.style.willChange = 'auto';
    };
    
    // Auto cleanup after 1 second
    setTimeout(cleanup, 1000);
    
    return cleanup;
  },

  // Batch animation updates
  batchAnimations: (() => {
    let batchQueue: (() => void)[] = [];
    let isProcessing = false;
    
    const processBatch = () => {
      if (isProcessing || batchQueue.length === 0) return;
      
      isProcessing = true;
      requestAnimationFrame(() => {
        const currentBatch = [...batchQueue];
        batchQueue = [];
        
        currentBatch.forEach(callback => {
          try {
            callback();
          } catch (error) {
            console.error('Batch animation error:', error);
          }
        });
        
        isProcessing = false;
        
        // Process any new items that were added during processing
        if (batchQueue.length > 0) {
          processBatch();
        }
      });
    };
    
    return (callback: () => void) => {
      batchQueue.push(callback);
      processBatch();
    };
  })(),

  // Optimize scroll animations
  optimizeScrollAnimation: (target: number, duration: number = 300) => {
    if (typeof window === 'undefined') return;
    
    const start = window.pageYOffset;
    const distance = target - start;
    const startTime = performance.now();
    
    const easeInOutCubic = (t: number) => {
      return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
    };
    
    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      window.scrollTo(0, start + distance * easeInOutCubic(progress));
      
      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };
    
    requestAnimationFrame(animateScroll);
  }
};

// Network optimization - محسن للشبكة
export const networkOptimization = {
  // Optimize image loading
  optimizeImageLoading: (img: HTMLImageElement) => {
    if (typeof window === 'undefined') return;
    
    // Use Intersection Observer for lazy loading
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          img.src = img.dataset.src || img.src;
          observer.unobserve(img);
        }
      });
    });
    
    observer.observe(img);
    
    return () => observer.unobserve(img);
  },

  // Prefetch critical routes
  prefetchCriticalRoutes: () => {
    if (typeof window === 'undefined') return;
    
    const criticalRoutes = [
      '/age-calculator',
      '/date-difference',
      '/resume',
      '/greeting-cards'
    ];

    criticalRoutes.forEach(route => {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = route;
      document.head.appendChild(link);
    });
  }
};

// Initialize all optimizations - تهيئة جميع التحسينات
export const initializeOptimizations = () => {
  if (typeof window === 'undefined') return;

  // Register service worker
  registerServiceWorker();

  // Preload critical resources
  bundleOptimization.preloadCritical();

  // Prefetch critical routes
  networkOptimization.prefetchCriticalRoutes();

  // Optimize scroll events with reduced frequency for mobile
  const optimizedScrollHandler = memoryManagement.throttleScroll(() => {
    // Handle scroll events efficiently
  });

  window.addEventListener('scroll', optimizedScrollHandler, { passive: true });

  // Cleanup on page unload
  window.addEventListener('beforeunload', () => {
    memoryManagement.cleanup();
    window.removeEventListener('scroll', optimizedScrollHandler);
  });

  console.log('Performance optimizations initialized');
}; 